import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { Contact } from '~/api/clients/types';

import { type OrganisationClient } from '~/api/clients/types';
import { AlertDialog, useTable } from '~/components';
import { r, routes } from '~/providers/RouterProvider/router.routes';

import { ConfirmDeleteContactDialog } from './ConfirmDeleteContactDialog';
import { useContactsTableColumns } from './ContactsTable.hooks';

type Props = {
  client: OrganisationClient;
};

export const ContactsTable = ({ client }: Props) => {
  const deleteContactHandle = AlertDialog.useHandle<Contact>();
  const dialogHandles = useMemo(() => ({ deleteContactHandle }), [deleteContactHandle]);

  const data = useMemo(() => client.contacts.filter(({ deleted }) => !deleted), [client.contacts]);
  const columns = useContactsTableColumns({ dialogHandles });
  const [table, Table] = useTable({ data, columns });

  const navigate = useNavigate();
  const { t } = useTranslation(['clients', 'common']);

  return (
    <>
      <Table.Root table={table}>
        <Table.Grid>
          <Table.Header />
          <Table.Body>
            {(row) => (
              <Table.Row
                actionLabel={t('common:actions.edit')}
                onClick={(contact) => navigate(r(routes.editContact, { clientId: client.id, contactId: contact.id }))}
                row={row}
              />
            )}
          </Table.Body>
        </Table.Grid>

        <Table.Pagination />
      </Table.Root>

      <AlertDialog.Root handle={deleteContactHandle}>
        {({ payload: contact }) =>
          contact && <ConfirmDeleteContactDialog client={client} contact={contact} onClose={() => deleteContactHandle.close()} />
        }
      </AlertDialog.Root>
    </>
  );
};
